import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, Copy, Check, LogOut, Users, IndianRupee, Wallet, Loader2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import marketerApi, { MARKETER_TOKEN_KEY } from '../../lib/marketerApi';
import BrandLogo from '../../components/brand/BrandLogo';

const inr = (n) => (n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const COMM_CLS = {
  ACCRUED: 'bg-emerald-100 text-emerald-700',
  NEEDS_ADMIN_REVIEW: 'bg-amber-100 text-amber-700',
  REVERSED: 'bg-rose-100 text-rose-600',
  PAID: 'bg-sky-100 text-sky-700',
};

export default function MarketerDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [byMonth, setByMonth] = useState([]);
  const [copied, setCopied] = useState(false);
  const [bank, setBank] = useState({ bank_name: '', bank_ifsc: '', bank_account: '', upi_id: '' });
  const [savingBank, setSavingBank] = useState(false);

  const load = useCallback(() => {
    marketerApi.get('/affiliate/me').then(({ data }) => {
      setMe(data);
      setBank({ bank_name: data.bank_name || '', bank_ifsc: data.bank_ifsc || '', bank_account: '', upi_id: data.upi_id || '' });
    }).catch(() => nav('/affiliate'));
    marketerApi.get('/affiliate/me/sellers').then(({ data }) => setSellers(data.sellers || [])).catch(() => {});
    marketerApi.get('/affiliate/me/commissions').then(({ data }) => setCommissions(data.commissions || [])).catch(() => {});
    marketerApi.get('/affiliate/me/payouts').then(({ data }) => setPayouts(data.payouts || [])).catch(() => {});
    marketerApi.get('/affiliate/me/statement').then(({ data }) => setByMonth(data.by_month || [])).catch(() => {});
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem(MARKETER_TOKEN_KEY)) { nav('/affiliate'); return; }
    load();
  }, [load, nav]);

  const logout = () => { localStorage.removeItem(MARKETER_TOKEN_KEY); nav('/affiliate'); };

  const copyLink = () => {
    navigator.clipboard?.writeText(me.referral_link);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      const payload = Object.fromEntries(Object.entries(bank).filter(([, v]) => v !== ''));
      const { data } = await marketerApi.put('/affiliate/me/bank', payload);
      setMe((m) => ({ ...m, ...data }));
      setBank((b) => ({ ...b, bank_account: '' }));
      toast.success('Payout details saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingBank(false);
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center bg-fab-elevated"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>;

  const s = me.summary || {};

  return (
    <div className="min-h-screen bg-fab-elevated" data-testid="marketer-dashboard">
      <header className="bg-white border-b border-fab-primary-lighter sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo height={26} />
            <span className="text-[10px] uppercase tracking-widest text-fab-text-muted font-semibold">Marketer</span>
          </div>
          <button onClick={logout} data-testid="marketer-logout" className="flex items-center gap-1.5 text-[13px] text-fab-text-secondary hover:text-fab-text"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-fab-text flex items-center gap-2"><Handshake className="w-6 h-6 text-fab-primary" /> Hi {me.name}</h1>
          <p className="text-[13px] text-fab-text-secondary mt-0.5">{me.type === 'PROFESSIONAL' ? 'Professional' : 'Community'} marketer · earning <b>{me.rate_pct}%</b> on every referred subscription.</p>
        </div>

        {/* Referral link + QR */}
        <div className="fab-card p-5 flex flex-col sm:flex-row items-center gap-5">
          <img src={me.qr_data_url} alt="Referral QR" className="w-32 h-32 rounded-lg border border-fab-primary-lighter" data-testid="marketer-qr" />
          <div className="flex-1 w-full">
            <div className="text-[12px] uppercase tracking-wide text-fab-text-muted font-semibold mb-1">Your referral link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={me.referral_link} data-testid="marketer-referral-link" className="fab-input flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <button onClick={copyLink} data-testid="marketer-copy-link" className="btn-primary px-3 py-2 flex items-center gap-1 text-[13px]">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy
              </button>
            </div>
            <p className="text-[12px] text-fab-text-muted mt-2">Share this link or QR. Sellers who sign up through it are credited to you — permanently.</p>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Referred Sellers', value: sellers.length, icon: Users },
            { label: 'Total Earned', value: inr(s.total_accrued), icon: IndianRupee },
            { label: 'Paid Out', value: inr(s.total_paid_out), icon: Wallet },
            { label: 'Balance Due', value: inr(s.balance_due), icon: IndianRupee },
          ].map((c) => (
            <div key={c.label} className="fab-card p-4" data-testid={`marketer-stat-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <c.icon className="w-4 h-4 text-fab-primary mb-1.5" />
              <div className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">{c.label}</div>
              <div className="text-lg font-bold text-fab-text mt-0.5">{c.value}</div>
            </div>
          ))}
        </div>

        {s.needs_review_count > 0 && (
          <div className="flex items-center gap-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4" /> {s.needs_review_count} commission(s) are pending verification by the FABTEXS team and will become payable once confirmed.
          </div>
        )}

        {/* Referred sellers */}
        <div className="fab-card p-5">
          <div className="text-[14px] font-semibold text-fab-text mb-3">Referred Sellers</div>
          {sellers.length === 0 ? <div className="text-[13px] text-fab-text-muted">No referrals yet. Share your link to get started.</div> : (
            <div className="space-y-2">
              {sellers.map((s2, i) => (
                <div key={i} className="flex items-center justify-between text-[13px] border-b border-fab-primary-lighter/60 pb-2">
                  <span className="font-medium text-fab-text">{s2.seller_name}</span>
                  <span className="flex items-center gap-2 text-fab-text-muted">
                    <span>{s2.joined_month}</span>
                    {s2.subscribed && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[11px] font-semibold">Subscribed</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly earnings */}
        {byMonth.length > 0 && (
          <div className="fab-card p-5" data-testid="marketer-monthly">
            <div className="text-[14px] font-semibold text-fab-text mb-3">Monthly Earnings</div>
            <div className="space-y-2">
              {byMonth.map((mo) => (
                <div key={mo.month} className="flex items-center justify-between text-[13px] border-b border-fab-primary-lighter/60 pb-2" data-testid={`marketer-month-${mo.month}`}>
                  <span className="font-medium text-fab-text">{mo.month}</span>
                  <span className="flex items-center gap-3 text-fab-text-muted">
                    <span>{mo.count} commission{mo.count === 1 ? '' : 's'}</span>
                    <span className="font-semibold text-fab-text">{inr(mo.commission)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commissions */}
        <div className="fab-card p-5">
          <div className="text-[14px] font-semibold text-fab-text mb-3">Commission Statement</div>
          {commissions.length === 0 ? <div className="text-[13px] text-fab-text-muted">No commissions yet.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="text-fab-text-muted text-[11px] uppercase">
                  <tr><th className="text-left py-1.5">Seller</th><th className="text-left">Period</th><th className="text-right">Base (ex-GST)</th><th className="text-right">Commission</th><th className="text-center">Status</th></tr>
                </thead>
                <tbody>
                  {commissions.map((c, i) => (
                    <tr key={i} className="border-t border-fab-primary-lighter/60" data-testid={`marketer-commission-${i}`}>
                      <td className="py-2">{c.seller_name}</td>
                      <td>{(c.period_start || '').slice(0, 10)}</td>
                      <td className="text-right">{inr(c.base_amount)}</td>
                      <td className="text-right font-semibold">{inr(c.commission_amount)}</td>
                      <td className="text-center"><span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${COMM_CLS[c.status] || 'bg-neutral-100'}`}>{c.status === 'NEEDS_ADMIN_REVIEW' ? 'PENDING' : c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payouts */}
        {payouts.length > 0 && (
          <div className="fab-card p-5">
            <div className="text-[14px] font-semibold text-fab-text mb-3">Payouts Received</div>
            <div className="space-y-2">
              {payouts.map((p, i) => (
                <div key={i} className="flex justify-between text-[13px] border-b border-fab-primary-lighter/60 pb-2">
                  <span>{p.period_label || (p.created_at || '').slice(0, 10)} {p.reference ? `· ${p.reference}` : ''}</span>
                  <span className="font-semibold">{inr(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bank / payout details */}
        <div className="fab-card p-5">
          <div className="text-[14px] font-semibold text-fab-text mb-1">Payout Details</div>
          <p className="text-[12px] text-fab-text-muted mb-3">Payouts are processed manually by the FABTEXS team. Keep these up to date.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} placeholder="Bank name" data-testid="bank-name" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={bank.bank_ifsc} onChange={(e) => setBank({ ...bank, bank_ifsc: e.target.value })} placeholder="IFSC" data-testid="bank-ifsc" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={bank.bank_account} onChange={(e) => setBank({ ...bank, bank_account: e.target.value })} placeholder={me.bank_account_masked ? `Account (${me.bank_account_masked})` : 'Account number'} data-testid="bank-account" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={bank.upi_id} onChange={(e) => setBank({ ...bank, upi_id: e.target.value })} placeholder="UPI ID" data-testid="bank-upi" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]" />
          </div>
          <button onClick={saveBank} disabled={savingBank} data-testid="bank-save" className="btn-primary px-4 py-2 text-[13px] mt-3">{savingBank ? 'Saving…' : 'Save Payout Details'}</button>
        </div>
      </main>
    </div>
  );
}
